import type { CSSProperties, ReactElement } from 'react';

/**
 * phase-compiler-chrome-batch item 2 — THE stacked preview navigation cluster,
 * extracted from DocumentPreviewPane (where phase-seed-state-audit-and-
 * preview-nav built it) so every preview header renders the same geometry:
 *
 *   ◀ ▶            ← DOCUMENT arrows (top row)
 *   ◀ ▶ Page x of y ← PAGE arrows directly underneath, label BESIDE them
 *
 * Purely presentational — the OWNERS keep the behaviour contracts:
 *   - the 8982e7f rule (the page label follows DISPLAYED reality: it advances
 *     on the iframe's onLoad, never on the click) lives in the caller's
 *     shownPage state, passed here as `pageLabel`;
 *   - the unknowable-count mode (arrows without a total) is the caller passing
 *     a label with no total and `atLastPage=false`.
 *
 * Consumed by DocumentPreviewPane (Submission Details) and
 * CompilerPreviewPanel (Compiler Workspace Preview tab) — change it once,
 * every surface inherits.
 */

const navBtnStyle = (enabled: boolean): CSSProperties => ({
  padding: '2px 8px',
  border: '1px solid var(--border)',
  borderRadius: 4,
  background: 'var(--surface)',
  cursor: enabled ? 'pointer' : 'not-allowed',
  opacity: enabled ? 1 : 0.4,
});

export interface PreviewNavClusterProps {
  /** Prefix for every data-testid (legacy DocumentPreviewPane ids use "preview"). */
  readonly testIdPrefix?: string;

  // ── Document row ────────────────────────────────────────────────────────
  /**
   * phase-workspace-naming-and-preview-cluster — a surface that deliberately
   * has no document-flipping (the Inspector's preview: it IS the selected
   * document's companion view) hides the doc row via this EXPLICIT prop —
   * never via a divergent copy of the cluster.
   */
  readonly showDocumentRow?: boolean;
  readonly onPrevDocument?: (() => void) | undefined;
  readonly onNextDocument?: (() => void) | undefined;
  readonly hasPrevDocument: boolean;
  readonly hasNextDocument: boolean;

  // ── Page row (hidden entirely when `showPageControl` is false) ──────────
  readonly showPageControl: boolean;
  /** True when the total page count is known (drives data-count-known). */
  readonly countKnown?: boolean;
  readonly atFirstPage?: boolean;
  /** Unknowable-count mode passes false — ▶ stays enabled; the native viewer
   *  clamps at the real end and the label only follows a confirmed load. */
  readonly atLastPage?: boolean;
  readonly onPrevPage?: (() => void) | undefined;
  readonly onNextPage?: (() => void) | undefined;
  /** e.g. "Page 2 of 5", or "Page 2" in unknowable-count mode. */
  readonly pageLabel?: string;
}

export function PreviewNavCluster({
  testIdPrefix = 'preview',
  showDocumentRow = true,
  onPrevDocument,
  onNextDocument,
  hasPrevDocument,
  hasNextDocument,
  showPageControl,
  countKnown = true,
  atFirstPage = true,
  atLastPage = true,
  onPrevPage,
  onNextPage,
  pageLabel = '',
}: PreviewNavClusterProps): ReactElement {
  return (
    <div
      data-testid={`${testIdPrefix}-nav-cluster`}
      style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}
    >
      {showDocumentRow && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button
          type="button"
          onClick={onPrevDocument}
          disabled={!hasPrevDocument}
          aria-label="previous document"
          data-testid={`${testIdPrefix}-prev-btn`}
          style={navBtnStyle(hasPrevDocument)}
        >
          ◀
        </button>
        <button
          type="button"
          onClick={onNextDocument}
          disabled={!hasNextDocument}
          aria-label="next document"
          data-testid={`${testIdPrefix}-next-btn`}
          style={navBtnStyle(hasNextDocument)}
        >
          ▶
        </button>
      </div>
      )}
      {showPageControl && (
        <div
          data-testid={`${testIdPrefix}-page-control`}
          data-count-known={countKnown ? 'true' : 'false'}
          style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}
        >
          <button
            type="button"
            onClick={onPrevPage}
            disabled={atFirstPage}
            aria-label="previous page"
            data-testid={`${testIdPrefix}-page-prev`}
            style={navBtnStyle(!atFirstPage)}
          >
            ◀
          </button>
          <button
            type="button"
            onClick={onNextPage}
            disabled={atLastPage}
            aria-label="next page"
            data-testid={`${testIdPrefix}-page-next`}
            style={navBtnStyle(!atLastPage)}
          >
            ▶
          </button>
          <span data-testid={`${testIdPrefix}-page-label`} style={{ color: 'var(--text-muted)' }}>
            {pageLabel}
          </span>
        </div>
      )}
    </div>
  );
}
